<template>
    <div
        class="redesign-dashboard-menu"
        data-testid="menu-dashboard-container"
    >
        <div
            v-if="activeDashboard"
            class="flex relative dashboard-title_wrapper"
        >
            <RedesignBasePopover
                :value="showMenu"
                :disabled="editMode"
                :visible-arrow="false"
                :popper-class="`dashboard-menu-popover ${getDashboardMenuWrapperUniqueClass()}`"
                trigger="click"
                width="204"
                @show="showMenu = true"
                @hide="showMenu = false"
            >
                <template #reference>
                    <button
                        :disabled="editMode || loading"
                        :class="{'opacity-50': editMode}"
                        data-testid="menu-button-dashboard"
                        class="flex w-full items-center rounded cursor-pointer focus:ring-1 focus:ring-active-elements focus:border-active-elements justify-between px-3 redesign-dashboard-menu--button outline-0"
                    >
                        <div
                            v-if="loading"
                            class="absolute w-full h-full left-0 top-0"
                        >
                            <div class="main-bg-light w-full h-full opacity-70" />
                            <RedesignBaseSpinner class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
                        </div>

                        <div
                            class="text-main-sm font-semibold text-color truncate flex gap-2 items-center"
                            :class="{
                                'w-40': lg,
                                'w-24': !lg
                            }"
                        >
                            <span
                                v-if="isEditActionVisible"
                                class="settings-icon-wrapper p-1 flex items-center justify-center"
                                data-testid="menu-dashboard-button-settings"
                                @click.stop.prevent="manageLayout"
                            >
                                <RedesignBaseIcon
                                    name="Settings"
                                    :size="16"
                                    class="text-primary-actions"
                                />
                            </span>
                            <span class="" data-testid="menu-dashboard-title">{{ dashboardTitle }}</span>
                        </div>
                        <div class="flex">
                            <redesign-base-icon
                                name="ChevronDown"
                                class="text-primary transition"
                                :class="{'is-expanded': showMenu}"
                            />
                        </div>
                    </button>
                </template>
                <div class="menu-wrapper">
                    <div
                        v-if="isDesktop"
                        class="py-2 px-1 text-primary cursor-pointer flex items-center"
                        @click="newDashboard"
                    >
                        <redesign-base-icon
                            name="PlusSquare"
                            class="text-primary"
                        />
                        <span class="mx-2">{{ $t('common.newDashboard') }}</span>
                    </div>
                    <div
                        v-if="isDesktop && allDashboards.length > 0"
                        class="py-2 px-1 text-primary cursor-pointer flex items-center border-t"
                        @click="openReorderModal"
                    >
                        <redesign-base-icon
                            name="ArrowUpDown"
                            class="text-primary"
                        />
                        <span class="mx-2">{{ $t('common.reorderDashboards') }}</span>
                    </div>
                    <div
                        v-if="allDashboards.length > MIN_SEARCH_LENGTH"
                        class="dashboards-search_wrapper border-t py-1"
                    >
                        <RedesignBaseInput
                            v-model="searchModel"
                            :placeholder="String($t('general.search'))"
                            prefix-icon="Search"
                            size="small"
                        />
                    </div>
                    <div class="dashboards">
                        <div
                            v-for="dashboard in searchedDashboards"
                            :id="makeDashboardOptionId(dashboard.DashboardID)"
                            :key="dashboard.DashboardID"
                            data-testid="dashboard-item"
                            class="flex flex-row items-center justify-between py-2 px-1 cursor-pointer border-t text-color"
                            @click="chooseDashboard(dashboard)"
                        >
                            <div
                                class="flex-1 hover:text-primary"
                                :class="{'text-primary': +(dashboard.DashboardID || 0) === +activeDashboard}"
                            >
                                <template
                                    v-if="searchModel && dashboard.DashboardTitle?.toLowerCase().includes(searchModel)"
                                >
                                    <span
                                        :class="{ 'font-bold': dashboard.DashboardID === focusedDashboardId }"
                                        v-html="formatDashboardTitleToSearchModel(dashboard.DashboardTitle || '')"
                                    />
                                </template>
                                <template v-else>
                                    <span>{{ dashboard.DashboardTitle || '' }}</span>
                                </template>
                            </div>
                        </div>
                    </div>
                </div>
            </RedesignBasePopover>
        </div>
        <div
            v-else
            class="flex items-center justify-center"
        >
            <span
                class="text-color font-bold text-md"
            >
                {{ $t('common.newDashboard') }}
            </span>
            <el-tooltip
                class="item"
                effect="dark"
                :content="$t('common.newDashboard')"
                placement="top"
            >
                <span
                    class="py-1 px-3 text-primary cursor-pointer flex items-center"
                    @click="newDashboard"
                >
                    <redesign-base-icon
                        :size="22"
                        name="PlusSquare"
                        class="text-primary"
                    />
                </span>
            </el-tooltip>
        </div>
        <RedesignDashboardReorderModal
            :visible="showReorderModal"
            :dashboards="allDashboards"
            @close="closeReorderModal"
            @saved="onReorderSaved"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { debouncedWatch } from '@vueuse/core'
import { useRouter } from 'vue-router/composables'
import { Tooltip as ElTooltip } from 'element-ui'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import RedesignBasePopover from '@/components/RedesignBaseComponents/RedesignBasePopover.vue'
import { IDashboard } from '@/types/dashboard'
import { isDesktop, lg } from '@/composables/useBreakpoints'
import RedesignBaseSpinner from '@/components/RedesignBaseComponents/RedesignBaseSpinner.vue'
import { clearRequestInfo } from '@/composables/useAxiosRequestDuration'
import {
    ActiveAccountDashboards,
    ActiveDashboard,
    ActiveDashboardName,
    selectDashboard
} from '@/composables/globalData/useDashoardsData'
import RedesignBaseInput from '@/components/RedesignBaseComponents/Inputs/RedesignBaseInput.vue'
import RedesignDashboardReorderModal from './RedesignDashboardReorderModal.vue'

const MIN_SEARCH_LENGTH = 7

const router = useRouter()

const props = defineProps<{
    editMode?: boolean,
    hideSettings?: boolean
}>()

const showMenu = ref<boolean>()
const loading = ref(false)
const searchModel = ref('')
const debouncedSearchModel = ref('')
const uid = Math.random().toString(36).substring(7)
const focusedDashboardId = ref(-1)
const dashboardOptionIdPrefix = 'dashboard-option-'
const showReorderModal = ref(false)

/* Computed */
const activeDashboard = computed(() => {
    return ActiveDashboard.value
})
const dashboardTitle = computed(() => {
    return ActiveDashboardName.value
})
const allDashboards = computed(() => {
    return ActiveAccountDashboards.value
})
const searchedDashboards = computed(() => {
    if (!debouncedSearchModel.value) {
        return allDashboards.value
    }
    return allDashboards.value.filter(d => d.DashboardTitle?.toLowerCase().includes(searchModel.value.toLowerCase()))
})
const isEditActionVisible = computed(() => {
    return isDesktop.value && !props.hideSettings
})

/* Methods */
function newDashboard () {
    router.push('/dashboard-creation')
}

function manageLayout () {
    if (props.editMode) {
        return
    }
    router.push('/dashboard-settings')
}

function getDashboardMenuWrapperUniqueClass () {
    return `dashboard-menu-wrapper-${uid}`
}

function makeDashboardOptionId (dashboardId: number | undefined) {
    return `${dashboardOptionIdPrefix}${dashboardId || 0}`
}

async function chooseDashboard (dashboard: IDashboard) {
    showMenu.value = false
    searchModel.value = ''
    loading.value = true
    await selectDashboard(dashboard.DashboardID)
    clearRequestInfo()

    loading.value = false
}

function formatDashboardTitleToSearchModel (dashboardTitle: string) {
    const searchModelLower = searchModel.value.toLowerCase()
    const index = dashboardTitle.toLowerCase().indexOf(searchModelLower)
    const length = searchModelLower.length

    return `${dashboardTitle.slice(0, index)}<span class="text-primary font-bold">${dashboardTitle.slice(index, index + length)}</span>${dashboardTitle.slice(index + length)}`
}

function openReorderModal () {
    showMenu.value = false
    showReorderModal.value = true
}

function closeReorderModal () {
    showReorderModal.value = false
}

function onReorderSaved () {
    closeReorderModal()
}

/* Watcher */
debouncedWatch(
    searchModel,
    (val: string) => {
        debouncedSearchModel.value = val
    },
    { debounce: 300 }
)
</script>

<style lang="scss">
.redesign-dashboard-menu {
    @apply relative;
    &--button {
        @apply border border-ui-lines rounded h-9;
    }

    .settings-icon-wrapper {
        @apply rounded;
        &:hover {
            color: color-mix(in srgb, var(--primary-actions) 80%, #000);
            background-color: color-mix(in srgb, var(--primary-actions) 20%, transparent);
            transition: all 0.2s ease-in;
        }
    }

    .transition {
        transition: all 0.2s ease-in;
    }

    .text-color {
        color: var(--mainTextColor-color);
    }

    .is-expanded {
        transform: rotate(-180deg);
    }
}

.dashboards-search_wrapper {
    position: sticky;
    top: -10px;
    background-color: var(--mainBgColor-color);
}

.redesign-popover.dashboard-menu-popover {
    @apply max-h-64 overflow-y-auto pb-0;
}
</style>
